import { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import App from "./App";
import LoginPage from "./pages/LoginPage";
import { useAuth, hasRole, type Role } from "./auth/AuthProvider";
import { ButtonLink, Spinner } from "./ui";
import Icon from "./components/Icon";

// Код-сплиттинг: каждая страница — отдельный чанк, грузится по требованию.
// Базовый бандл (каркас + логин) остаётся маленьким.
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ProcessListPage = lazy(() => import("./pages/ProcessListPage"));
const BoardPage = lazy(() => import("./pages/board/BoardPage"));
const ProcessDetailPage = lazy(() => import("./pages/ProcessDetailPage"));
const ApprovalsPage = lazy(() => import("./pages/ApprovalsPage"));
const SuppliersPage = lazy(() => import("./pages/SuppliersPage"));
const AuditPage = lazy(() => import("./pages/AuditPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AdminApprovalRoutesPage = lazy(() => import("./pages/AdminApprovalRoutesPage"));
const AdminLlmPage = lazy(() => import("./pages/AdminLlmPage"));
const AdminLdapPage = lazy(() => import("./pages/AdminLdapPage"));
const AdminApprovalGroupsPage = lazy(() => import("./pages/AdminApprovalGroupsPage"));
const PrintRequestPage = lazy(() => import("./pages/print/PrintRequestPage"));
const VerifyCodePage = lazy(() => import("./pages/VerifyCodePage"));
const PrintProcurementPage = lazy(() => import("./pages/print/PrintProcurementPage"));
const AiRoutesPage = lazy(() => import("./pages/AiRoutesPage"));
const ProductCategoriesPage = lazy(() => import("./pages/ProductCategoriesPage"));
const AdminUnitsPage = lazy(() => import("./pages/AdminUnitsPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const DepartmentsPage = lazy(() => import("./pages/DepartmentsPage"));
const AdminObjectsPage = lazy(() => import("./pages/AdminObjectsPage"));
const AdminBrandPage = lazy(() => import("./pages/AdminBrandPage"));
const AdminUsagePage = lazy(() => import("./pages/AdminUsagePage"));
const AdminProcessTemplatesPage = lazy(() => import("./pages/AdminProcessTemplatesPage"));
const LegalEntitiesPage = lazy(() => import("./pages/LegalEntitiesPage"));
const WarehousePage = lazy(() => import("./pages/WarehousePage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const MailPage = lazy(() => import("./pages/MailPage"));
const MailMessagePage = lazy(() => import("./pages/MailMessagePage"));
const EmailAccountsPage = lazy(() => import("./pages/EmailAccountsPage"));
const TendersPage = lazy(() => import("./pages/TendersPage"));
const TenderDetailPage = lazy(() => import("./pages/TenderDetailPage"));
const ItemBoardAttentionPage = lazy(() => import("./pages/ItemBoardAttentionPage"));
const RequestsPage = lazy(() => import("./pages/RequestsPage"));
const PurchaseRequestsPage = lazy(() => import("./pages/PurchaseRequestsPage"));
const RequestNewPage = lazy(() => import("./pages/RequestNewPage"));
const RequestDetailPage = lazy(() => import("./pages/RequestDetailPage"));
const PortalRegisterPage = lazy(() => import("./pages/portal/RegisterPage"));
const AccountTokenPage = lazy(() => import("./pages/AccountTokenPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PortalLayout = lazy(() => import("./pages/portal/PortalLayout"));
const PortalItemsPage = lazy(() => import("./pages/portal/PortalItemsPage"));
const PortalOffersPage = lazy(() => import("./pages/portal/PortalOffersPage"));
const PortalMessagesPage = lazy(() => import("./pages/portal/PortalMessagesPage"));
const PortalAwardsPage = lazy(() => import("./pages/portal/PortalAwardsPage"));
const PortalProfilePage = lazy(() => import("./pages/portal/PortalProfilePage"));

/** Страница грузится отдельным чанком: показываем её форму, а не надпись. */
function PageFallback() {
  return (
    <div className="mx-auto w-full max-w-[100rem] space-y-4 px-4 py-5 sm:px-6 lg:px-8">
      <div className="skeleton h-7 w-56" />
      <div className="skeleton h-4 w-96" />
      <div className="skeleton h-64 w-full rounded-lg" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={22} className="text-faint" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function RequireRole({ roles, children }: { roles: Role[]; children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!hasRole(user, ...roles)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** «Отчёты»: администратору всегда, остальным — по выданному доступу. */
function RequireReports({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!user.reports_access) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Портал поставщика — только роль supplier. */
function RequireSupplier({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={22} className="text-faint" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "supplier") return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Склад — роль warehouse (и админ, чтобы посмотреть экран). */
function RequireWarehouse({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={22} className="text-faint" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "warehouse" && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/portal/register" element={<PortalRegisterPage />} />
      {/* Ссылки из писем (T5): приглашение и смена пароля — без входа. */}
      <Route path="/invite/:token" element={<AccountTokenPage />} />
      <Route path="/reset-password/:token" element={<AccountTokenPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* Склад живёт вне общего каркаса: на планшете не нужны меню
          закупок, а лишние разделы только мешают пальцу. */}
      {/* Печатные формы — без меню и каркаса: это лист, а не экран. Права
          проверяет сервер. */}
      <Route
        path="/requests/:id/print"
        element={
          <RequireAuth>
            <PrintRequestPage />
          </RequireAuth>
        }
      />
      <Route
        path="/processes/:id/print"
        element={
          <RequireAuth>
            <PrintProcurementPage />
          </RequireAuth>
        }
      />
      <Route
        path="/warehouse"
        element={
          <RequireWarehouse>
            <WarehousePage />
          </RequireWarehouse>
        }
      />
      <Route
        path="/portal"
        element={
          <RequireSupplier>
            <PortalLayout />
          </RequireSupplier>
        }
      >
        <Route index element={<PortalItemsPage />} />
        <Route path="offers" element={<PortalOffersPage />} />
        <Route path="messages" element={<PortalMessagesPage />} />
        <Route path="awards" element={<PortalAwardsPage />} />
        <Route path="profile" element={<PortalProfilePage />} />
      </Route>
      <Route
        element={
          <RequireAuth>
            <App />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="processes" element={<ProcessListPage />} />
        <Route path="processes/board" element={<BoardPage />} />
        {/* Шахматка заявок (Ш-З): доступ по ролям, как у карточки заявки. */}
        <Route
          path="requests/board"
          element={
            <RequireRole roles={["operator", "approver", "purchaser", "admin", "analyst", "warehouse"]}>
              <BoardPage kind="requests" />
            </RequireRole>
          }
        />
        <Route path="processes/:id" element={<ProcessDetailPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="verify" element={<VerifyCodePage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="departments" element={<DepartmentsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route
          path="reports"
          element={
            <RequireReports>
              <ReportsPage />
            </RequireReports>
          }
        />
        <Route
          path="admin"
          element={
            <RequireRole roles={["admin"]}>
              <AdminPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/approval-routes"
          element={
            <RequireRole roles={["admin"]}>
              <AdminApprovalRoutesPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/ai-routes"
          element={
            <RequireRole roles={["admin"]}>
              <AiRoutesPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/legal-entities"
          element={
            <RequireRole roles={["admin"]}>
              <LegalEntitiesPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/objects"
          element={
            <RequireRole roles={["admin"]}>
              <AdminObjectsPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/brand"
          element={
            <RequireRole roles={["admin"]}>
              <AdminBrandPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/usage"
          element={
            <RequireRole roles={["admin"]}>
              <AdminUsagePage />
            </RequireRole>
          }
        />
        <Route
          path="admin/process-templates"
          element={
            <RequireRole roles={["admin"]}>
              <AdminProcessTemplatesPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/product-categories"
          element={
            <RequireRole roles={["admin"]}>
              <ProductCategoriesPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/units"
          element={
            <RequireRole roles={["admin"]}>
              <AdminUnitsPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/approval-groups"
          element={
            <RequireRole roles={["admin"]}>
              <AdminApprovalGroupsPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/ldap"
          element={
            <RequireRole roles={["admin"]}>
              <AdminLdapPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/llm"
          element={
            <RequireRole roles={["admin"]}>
              <AdminLlmPage />
            </RequireRole>
          }
        />
        <Route path="users" element={<UsersPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="mail" element={<MailPage />} />
        <Route path="mail/:id" element={<MailMessagePage />} />
        <Route path="email-accounts" element={<EmailAccountsPage />} />
        <Route path="tenders" element={<TendersPage />} />
        <Route path="tenders/:id" element={<TenderDetailPage />} />
        <Route
          path="portal-items"
          element={
            <RequireRole roles={["purchaser", "admin"]}>
              <ItemBoardAttentionPage />
            </RequireRole>
          }
        />
        {/* Заявки глазами закупщика: согласованные / выполненные / отменённые */}
        <Route
          path="purchase-requests"
          element={
            <RequireRole roles={["purchaser", "admin"]}>
              <PurchaseRequestsPage />
            </RequireRole>
          }
        />
        {/* Заявки руководителя подразделения */}
        <Route
          path="requests"
          element={
            <RequireRole roles={["operator", "admin"]}>
              <RequestsPage />
            </RequireRole>
          }
        />
        <Route
          path="requests/new"
          element={
            <RequireRole roles={["operator", "admin"]}>
              <RequestNewPage />
            </RequireRole>
          }
        />
        <Route
          path="requests/:id"
          element={
            // Доступ к заявке решает сервер: шаг маршрута может достаться
            // любой внутренней роли (кладовщику, аналитику).
            <RequireRole roles={["operator", "approver", "purchaser", "admin", "analyst", "warehouse"]}>
              <RequestDetailPage />
            </RequireRole>
          }
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-center p-6">
      <span className="flex h-14 w-14 items-center justify-center rounded-full border border-line bg-sunken text-faint">
        <Icon name="search" size={28} />
      </span>
      <h1 className="text-xl font-semibold">Страница не найдена</h1>
      <p className="text-sm text-muted">
        Такого адреса нет. Проверьте ссылку или вернитесь на главную.
      </p>
      <ButtonLink variant="primary" className="mt-2" to="/">
        На главную
      </ButtonLink>
    </div>
  );
}
